import React, { useState, useEffect } from 'react';
import { Button } from "@shared/ui/button";
import { Zap, Globe, ChevronDown, UserPlus, LogIn } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@shared/ui/dropdown-menu";

export const GuestPortalTopnav = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [currentLang, setCurrentLang] = useState(i18n.language || localStorage.getItem('appLanguage') || 'en');

  useEffect(() => {
    setCurrentLang(i18n.language || localStorage.getItem('appLanguage') || 'en');
  }, [i18n.language]);

  const handleLanguageChange = (lang: string) => {
    localStorage.setItem('appLanguage', lang);
    i18n.changeLanguage(lang);
    setCurrentLang(lang);
  };

  const getLinkClass = (path: string) => {
    // Exact match for home, startsWith for other paths so sub-routes stay active
    const isActive = path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
    return isActive 
      ? "bg-blue-50 text-blue-600 px-3 py-1.5 rounded-md transition-colors" 
      : "text-gray-600 hover:text-blue-600 px-3 py-1.5 transition-colors";
  };

  return (
    <div className="w-full bg-white border-b border-gray-200 z-50">
      <div className="flex items-center justify-between px-6 py-3 w-full">
        <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
          <div className="p-1.5 bg-blue-600 rounded-lg">
            <Zap className="text-white h-5 w-5" />
          </div>
          <div className="font-bold text-sm tracking-widest leading-tight text-gray-900">
            SMART360<br/><span className="text-gray-500 text-[10px]">UTILITY PORTAL</span>
          </div>
        </div>

        <nav className="hidden md:flex items-center gap-2 text-sm font-medium">
          <Link to="/" className={getLinkClass('/')}>{t("Home")}</Link>
          <Link to="/guest/pay-bill" className={getLinkClass('/guest/pay-bill')}>{t("Pay Bills")}</Link>
          <Link to="/guest/service-request" className={getLinkClass('/guest/service-request')}>{t("Services")}</Link>
          <Link to="/guest/nsc" className={getLinkClass('/guest/nsc')}>{t("New Connection")}</Link>
          <Link to="/guest/info-center" className={getLinkClass('/guest/info-center')}>{t("Info Center")}</Link>
        </nav>

        <div className="flex items-center gap-3">
          <Button variant="outline" className="text-sm font-medium h-9" onClick={() => navigate('/create-account')}>
            <UserPlus className="w-4 h-4 mr-2 text-gray-500" /> {t("Register")}
          </Button>
          <Button className="bg-blue-600 hover:bg-blue-700 text-sm font-medium h-9 text-white" onClick={() => navigate('/login')}>
            <LogIn className="w-4 h-4 mr-2" /> {t("Sign In")}
          </Button>
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-2 border border-gray-200 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 h-9 outline-none">
                <Globe className="w-4 h-4 text-gray-500" />
                {currentLang.startsWith('fr') ? (
                  <><span className="text-xs text-gray-500 font-bold">FR</span> FR</>
                ) : (
                  <><span className="text-xs text-gray-500 font-bold">US</span> EN</>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-[150px]">
              <DropdownMenuItem onClick={() => handleLanguageChange('en')} className="cursor-pointer">
                <span className="font-bold mr-2">US</span> English
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleLanguageChange('fr')} className="cursor-pointer">
                <span className="font-bold mr-2">FR</span> Français
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
};
